import React, { useMemo, useState, useEffect, useCallback } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import SectionHeader from "@cayuse/shared-react.components.section-header";

import BasePadding from "@cayuse/shared-react.components.base-padding";
import IconButton from "@cayuse/shared-react.components.icon-button";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import { getForm, resetForm } from "store/actions/form";
import { formSelectors, tenantSelectors } from "store/selectors";
import { RuleBuilderContextProvider } from "containers/Rules/context/RuleBuilderContext";
import { useDictionary, useFormRules, useComponentDidMount } from "hooks";
import { spUrl, getLastPublishedFormElements } from "utils";
import { FORMS_BASE_ROUTE, RULE_TYPES } from "globals/constants";

import RulesListView from "containers/RulesOld/List";
import ConditionButton from "../List/ConditionButton/ConditionButton";

import styles from "./styles.css";

const RulesMainView = () => {
    const { formId } = useParams();

    const form = useSelector(formSelectors.content, shallowEqual);
    const { infoReadyEnabled } = useSelector(tenantSelectors.tenant);

    const history = useHistory();
    const dictionary = useDictionary("ruleBuilder");
    const [rules, setRules] = useFormRules();
    const [newRuleOrder, setNewRuleOrder] = useState();
    const [editingCondition, setEditingCondition] = useState(false);
    const [formChanged, setFormChanged] = useState(false);
    const [isEditingAction, setEditingAction] = useState(false);
    const [isEditingOperand, setEditingOperand] = useState(false);
    const [ruleOrderIndexes, setRuleOrderIndexes] = useState(null);
    const [hasUnsavedOrderChanges, setHasUnsavedOrderChanges] = useState(false);
    const [lastPublishedFormElements, setLastPublishedFormElements] =
        useState();

    const [ruleTypeHash, setRuleTypeHash] = useState({
        [RULE_TYPES.AD_HOC_NOTIFICATION]: {
            label:
                dictionary.actionButtonLabels?.teamNotification ||
                "Team Notification Rule",
            type: RULE_TYPES.AD_HOC_NOTIFICATION,
        },
        [RULE_TYPES.AD_HOC_REVIEW]: {
            label:
                dictionary.actionButtonLabels?.teamReview || "Team Review Rule",
            type: RULE_TYPES.AD_HOC_REVIEW,
        },
        [RULE_TYPES.RT_CERTIFICATION]: {
            label:
                dictionary.actionButtonLabels?.researchTeamCertification ||
                "Certification Rule",
            type: RULE_TYPES.RT_CERTIFICATION,
        },
        [RULE_TYPES.RT_REVIEW]: {
            label:
                dictionary.actionButtonLabels?.researchTeamReview ||
                "Research Team Review Rule",
            type: RULE_TYPES.RT_REVIEW,
        },
        [RULE_TYPES.UNIT_REVIEW]: {
            label:
                dictionary.actionButtonLabels?.unitReview || "Unit Review Rule",
            type: RULE_TYPES.UNIT_REVIEW,
        },
        [RULE_TYPES.DISCLOSURE]: {
            label:
                dictionary.actionButtonLabels?.disclosure ||
                "Outside Interests Disclosure Rule",
            type: RULE_TYPES.DISCLOSURE,
        },
    });

    const componentDidMount = useComponentDidMount();

    useEffect(() => {
        /* reset form store on-mount and on-unmount */
        resetForm();

        return () => resetForm();
    }, []);

    useEffect(() => {
        resetForm();
        if (isNonEmptyString(formId) && componentDidMount) {
            const firstDashIndex = formId.indexOf("-");
            const trimmedId =
                firstDashIndex > -1
                    ? formId.substr(firstDashIndex + 1)
                    : formId;

            getForm(trimmedId);
        }
    }, [componentDidMount, formId]);

    useEffect(() => {
        if ("proposalForm" === form.modelKey && infoReadyEnabled === true) {
            const newRuleTyupeHash = { ...ruleTypeHash };
            newRuleTyupeHash[RULE_TYPES.INFOREADY_REVIEW] = {
                label:
                    dictionary.actionButtonLabels?.inforeadyReview ||
                    "InfoReady Review Rule",
                type: RULE_TYPES.INFOREADY_REVIEW,
            };

            setRuleTypeHash(newRuleTyupeHash);
        }
        // Adding ruleTypeHash to the dependiencies causes continuous re-render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dictionary.actionButtonLabels, infoReadyEnabled, form]);

    useEffect(() => {
        if (
            form.modelKey === "awardForm" &&
            RULE_TYPES.INFOREADY_REVIEW in ruleTypeHash
        ) {
            const newRuleTypeHash = { ...ruleTypeHash };
            delete newRuleTypeHash[RULE_TYPES.INFOREADY_REVIEW];
            setRuleTypeHash(newRuleTypeHash);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form]);

    const onRuleChange = (updatedRule) => {
        const updatedRules = rules.map((rule) => {
            return rule.id === updatedRule.id ? updatedRule : rule;
        });

        setRules(updatedRules);
    };

    const retrieveLastPublishedFormElements = useCallback(async (modelKey) => {
        const result = await getLastPublishedFormElements(modelKey);
        setLastPublishedFormElements(result);
    }, []);

    useEffect(() => {
        if (form && !form.published) {
            retrieveLastPublishedFormElements(form.modelKey);
        }
    }, [form]);

    const buttons = useMemo(() => {
        return (
            <>
                <IconButton
                    icon="reply"
                    labelPosition="left"
                    secondary
                    onClick={() =>
                        history.push(spUrl(`${FORMS_BASE_ROUTE}/${formId}`))
                    }
                >
                    {dictionary.backButtonLabel || "Back"}
                </IconButton>
                <ConditionButton disabled={editingCondition || isEditingAction} />
            </>
        );
    }, [editingCondition, isEditingAction]);

    const breadcrumbs = [
        {
            text: dictionary.breadcrumbs?.root || "Forms",
            link: spUrl(FORMS_BASE_ROUTE),
        },
        {
            text: form.name || "Form Template",
            link: spUrl(`${FORMS_BASE_ROUTE}/${form.modelId}`),
        },
        {
            text: dictionary.breadcrumbs?.rules || "Rules",
        },
    ];

    return (
        <div data-component="RulesMainView">
            <VerticalSpacing>
                <RuleBuilderContextProvider
                    rules={rules}
                    setRules={setRules}
                    onRuleChange={onRuleChange}
                    formVersion={form.version}
                    formElements={
                        lastPublishedFormElements || form.formElements
                    }
                    formSections={form.formSections}
                    formType={form.name?.split(" ").shift().toUpperCase()}
                    formId={formId}
                    ruleOrderIndexes={ruleOrderIndexes}
                    setRuleOrderIndexes={setRuleOrderIndexes}
                    hasUnsavedOrderChanges={hasUnsavedOrderChanges}
                    setHasUnsavedOrderChanges={setHasUnsavedOrderChanges}
                    ruleTypeHash={ruleTypeHash}
                    editingCondition={editingCondition}
                    setEditingCondition={setEditingCondition}
                    isEditingAction={isEditingAction}
                    setEditingAction={setEditingAction}
                    isEditingOperand={isEditingOperand}
                    setEditingOperand={setEditingOperand}
                    formChanged={formChanged}
                    setFormChanged={setFormChanged}
                    newRuleOrder={newRuleOrder} 
                    setNewRuleOrder={setNewRuleOrder}
                >
                    <SectionHeader
                        header="Rule Builder"
                        buttons={[buttons]}
                        breadcrumbs={breadcrumbs}
                    />
                    <BasePadding>
                        <div className={styles.layout}>
                            <RulesListView key={"rules-list-view"} />
                        </div>
                    </BasePadding>
                </RuleBuilderContextProvider>
            </VerticalSpacing>
        </div>
    );
};

export default RulesMainView;
